<template>
  <div class="container">
    <b-alert variant="danger" dismissible :show="!!error">
      <i class="fa fa-exclamation-triangle"></i>
      {{ error }}
    </b-alert>

    <div class="row">
      <div class="col"></div>
      <div class="col ml-auto mr-auto">
        <h1 class="pb-0 mb-1 mt-1">Projects</h1>
      </div>
      <div class="col mt-2">
        <div class="btn-toolbar float-right">
          <b-button
            class="btn btn-primary"
            title="Start a New CitSci Project"
            alt="Start a New CitSci Project"
            @click="onCreateClick"
          >
            <span>
              <i class="fa fa-plus"></i> <span class="label-text"><strong>Start a Project</strong></span>
            </span>
          </b-button>
        </div>
      </div>
    </div>

    <div class="container m-0 p-0 mb-3">
      <h2 class="mb-1 ml-1 mr-1 mt-0 pt-0">
        Featured Hubs
        <span class="p-down ml-3">
            <router-link :to="{ name: 'Hubs' }" :alt="'Hubs'" :title="'Hubs'">
              <span class="d-none d-md-inline">Learn more & view all</span>
              <i class="ml-1 fas fa-arrow-alt-circle-right" aria-hidden="true"></i>
            </router-link>
          </span>
      </h2>
      <div class="row d-flex align-items-stretch" v-if="!isOrganizationsLoading">
        <div v-for="featuredHub in featuredHubs"
             :key="featuredHub.id"
             class="col-md-2 mb-2 mx-0"
        >
          <div class="card p-0 mx-0">
            <div v-if="featuredHub.banner"
                 v-bind:style="{
                    'background-image': 'url(' + featuredHub.banner.path + ')',
                    height: '120px',
                    'background-color': '#cccccc',
                    'background-position': 'center',
                    'background-repeat': 'no-repeat',
                    'background-size': 'cover',
                }"
            ></div>
            <div v-else style="height: 120px; background-color: grey"></div>
            <router-link class="stretched-link"
                         :to="{name: 'OrganizationShow',params: { name: featuredHub.url },}"
                         :alt="featuredHub.name + ' Hub'"
                         :title="featuredHub.name + ' Hub'"
            >
            </router-link>
            <div class="card-footer pl-2 pr-0" style="background-color: #f5f4e8">
              <h6 class="d-none d-md-block">
                {{ ellipsizeCharacters(featuredHub.name, 25) }}
              </h6>
              <h5 class="d-md-none">{{ featuredHub.name }}</h5>
            </div>
          </div>
        </div>
      </div>
      <div class="" v-else>
        <i class="fa fa-spin fa-spinner fa-3x mr-3"></i> Loading Hubs...
      </div>
    </div>

    <div class="mb-3">
      <div class="row justify-center">
        <h2 class="mt-1 mb-1">Find Projects</h2>
      </div>
      <div class="row ml-2">
        <div class="col d-none d-lg-block"></div>
        <div class="col-10 col-lg-6 text-center ml-auto mr-auto search-box-border">
          <div class="input-group">
            <div class="input-group-prepend">
                <span class="btn pl-0 ml-0">
                  <i v-if="!isBusy" class="fas fa-search"></i>
                  <i v-if="isBusy" class="fas fa-spinner fa-spin"></i>
                </span>
            </div>
            <b-form-input
              class="form-control pl-0 no-border"
              placeholder="Search..."
              name="search"
              v-model="filterText"
              debounce="1500"
            ></b-form-input>
            <div v-show="filterText" class="input-group-append">
              <div class="btn rounded-pill" @click="filterText = null">
                <i class="fas fa-times-circle"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col mt-1">
          <div class="btn btn-tertiary btn-sm" @click="resetProjects()" title="Reset Search, Sort and Filters">Reset All
          </div>
        </div>
      </div>
    </div>

    <div class="row justify-content-between">
      <div class="col-12">
        <div class="p-1 slide-container mb-2">
          <div class="mr-auto ml-auto text-center mt-1">
            <h5>Filter by Topic <span v-show="this.filterTopic"> ({{ this.filterTopic }})</span></h5>
          </div>
          <vueper-slides
            class="no-shadow"
            fixed-height="100px"
            :visible-slides="8"
            slide-multiple
            :arrows="true"
            :arrows-outside="false"
            bullets-outside
            :gap="1"
            :dragging-distance="70"
            :breakpoints="{
                1200: { visibleSlides: 5, slideMultiple: 5, bulletsOutside: true },
                900: { visibleSlides: 3, slideMultiple: 3, bulletsOutside: true },
                600: { visibleSlides: 2, slideMultiple: 2, bulletsOutside: true },
                300: { visibleSlides: 1, slideMultiple: 1, bulletsOutside: true },
              }"
          >
            <template #arrow-left>
              <div class="slide-arrow">
                <i class="fas fa-chevron-left"></i>
              </div>
            </template>
            <template #arrow-right>
              <div class="slide-arrow">
                <i class="fas fa-chevron-right"></i>
              </div>
            </template>
            <vueper-slide class="rounded-lg slide-hover-unselected"
                          :class="{'slide-hover-selected': filterTopic === slide.title }"
                          v-for="slide in slidesData"
                          :key="slide.key"
                          link="#"
            >
              <template #content>
                <div @click="selectTopic(slide.title)"
                     class="d-flex flex-column align-items-center justify-content-center h-100 text-center">
                    <span class="d-flex align-items-center justify-content-center text-center mt-2">
                      <i :class="'fas fa-3x fa-' + slide.icon"></i>
                    </span>
                  <div style='font-size:16px'><b>{{ slide.content || slide.title }}</b></div>
                </div>
              </template>
            </vueper-slide>
          </vueper-slides>
        </div>
      </div>
    </div>

    <div class="container pt-0 pb-0">
      <div class="row justify-content-between p-2 mt-2 filter-container">
        <div class="col-sm-12 col-md-6 col-lg-3 ml-3 mt-1 pl-0">
          <span class="mt-2 mr-2">
            View:
          </span>
          <div class="btn-group">
            <div class="btn"
                 :class="viewMode === 'card' ? 'btn btn-tertiary' : 'btn btn-outline-secondary'"
                 id="CardViewButton"
                 title="Card View"
                 @click="viewMode = 'card'"
            >
              <i class="far fa-images fa-1x"></i> Card
            </div>
            <div class="btn"
                 :class="viewMode === 'list' ? 'btn btn-tertiary': 'btn btn-outline-secondary'"
                 id="ListViewButton"
                 title="List View"
                 @click="viewMode = 'list'"
            >
              <i class="fas fa-list"></i> List
            </div>
            <div class="btn btn-outline-secondary mr-0"
                 v-if="false"
                 type="button"
                 id="MapViewButton"
                 title="Map View (coming soon)"
                 disabled
            >
              <i class="fa fa-map-marker"></i>
            </div>
          </div>
        </div>

        <div class="input-group col-sm-12 col-md-6 col-lg-3 ml-3 mt-1 pl-0">
          <span class="mt-2 mr-2 text-muted"> Filter: </span>
          <b-form-select
            v-if="!isOrganizationsLoading"
            class="ml-1"
            v-model="filterOrg"
            :options="hubsArray"
            label="name"
            placeholder="Filter By Hub"
          >
          </b-form-select>
          <div v-show="filterOrg" class="input-group-append">
            <b-button :disabled="!filterOrg" @click="filterOrg = null">
              <i class="fas fa-times-circle"></i>
            </b-button>
          </div>
        </div>
        <div class="input-group col-sm-12 col-md-6 col-lg-3 ml-3 mt-1 pl-0">
          <span class="mt-2 mr-2 text-muted"> Sort: </span>
          <b-form-select
            class="ml-1"
            v-model="selectedSort"
            :options="sortOptions">
          </b-form-select>
        </div>
      </div>
    </div>

    <div class="row justify-content-between mt-4">
      <div class="my-1 col-md-3">
        Showing {{ fromItem }} to {{ toItem }} of {{ totalItems }} projects
      </div>

      <div class="col-md-3 my-1">
        <b-pagination
          :total-rows="totalItems"
          :per-page="perPage"
          v-model="currentPage"
          class="my-0 pagination-holder"
          prev-text="Prev"
          next-text="Next"
          align="fill"
        />
      </div>

      <div class="col-md-3 my-1">
        <b-form-group
          label="Per Page"
          label-align="right"
          label-cols-sm="7"
          label-cols-md="8"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
          >
          </b-form-select>
        </b-form-group>
      </div>
    </div>

    <b-table
      class="p-0 mb-2"
      id="projectsTable"
      ref="projectsTable"
      show-empty
      hover
      :sticky-header="true"
      stacked="lg"
      :items="projects"
      :fields="projectsListFields"
      :busy="isBusy"
      :projectState="this.PROJECT_STATE_PUBLISHED"
      :current-page="currentPage"
      :per-page="perPage"
      :total-rows="totalItems"
      :filter="filterVal"
      sort-icon-left
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
      :striped="false"
      :empty-text="emptyText"
      :empty-filtered-text="emptyFilteredText"
      @filtered="onFiltered"
      v-show="viewMode === 'list'"
    >
      <template v-slot:cell(photo)="row">
        <router-link
          :to="{ name: 'ProjectShow', params: { id: row.item.urlField } }"
          :id="'project-photo-field-' + row.item['id']"
          class="d-block photo-link mx-auto"
        >
          <b-row class="mx-auto fit-content">
            <b-col
              class="mx-0 px-0 overflow-hidden"
              style="max-height: 68px; max-width: 100px"
            >
              <!-- fit-content -->
              <b-img
                class="mx-0 h-100"
                rounded="0"
                :src="
                    row.item.picture
                      ? row.item.picture.path
                      : row.item.bannerPicture
                      ? row.item.bannerPicture.path
                      : '/assets/images/generic_img.png'
                  "
                alt="Project Image"
              />
            </b-col>
          </b-row>
        </router-link>
      </template>

      <template v-slot:cell(name)="row">
        <div>
          <router-link
            :id="'project-name-field-' + row.item['id']"
            class="d-inline align-bottom font-weight-bold"
            style="font-size: 1.25rem"
            :to="{ name: 'ProjectShow', params: { id: row.item.urlField } }"
            :alt="row.item.name"
            :title="row.item.name + ' citizen science project'"
          >
            {{ ellipsizeCharacters(row.item.name, 70) }}
          </router-link>
          <b-img
            :src="
                '/assets/images/' +
                (row.item && row.item.approveContacts ? 'closed' : 'open') +
                '-access.png'
              "
            width="20"
            height="20"
            :alt="
                (row.item && row.item.approveContacts
                  ? 'Member-Based/Closed'
                  : 'Open') + 'Membership - Requires approval by manager'
              "
            :title="
                (row.item && row.item.approveContacts
                  ? 'Member-Based/Closed'
                  : 'Open') + ' Membership - Requires approval by manager'
              "
            class="closed-opened-status d-inline"
            style="margin-top: -8px"
          />
          <b-img
            :src="
                '/assets/images/' +
                (row.item && row.item.isPrivate ? 'private' : 'public') +
                '.png'
              "
            width="20"
            height="20"
            :alt="
                (row.item && row.item.isPrivate ? 'Private' : 'Public') +
                ' Data'
              "
            :title="
                (row.item && row.item.isPrivate ? 'Private' : 'Public') +
                ' Data'
              "
            class="private-public-status d-inline ml-1"
            style="margin-top: -8px"
          />
        </div>

        <div
          style="height: 36px; max-width: 550px; line-height: 14px"
          class="ml-4 mt-1 p-down text-wrap"
        >
          <small>
            {{ ellipsizeCharacters(row.item.description, 170) }}
          </small>
        </div>
      </template>

      <template v-slot:cell(createdAt)="row">
        {{ row.item.createdAt ? displayDate(row.item.createdAt) : "-" }}
      </template>

      <template v-slot:cell(lastObservationDatetime)="row">
        {{ row.item.createdAt ? displayDate(row.item.lastObservationDatetime) : "-" }}
      </template>

      <template v-slot:cell(measurementsTotal)="row">
        {{ row.item.measurementsTotal ? row.item.measurementsTotal : 0 }}
      </template>

      <template v-slot:cell(options)="row" class="options-holder">
        <ProjectJoinButton v-bind:project="row.item"></ProjectJoinButton>
      </template>
      <template #table-busy>
        <VueLoader style="height: 200px" text="Loading Projects..."/>
      </template>
    </b-table>

    <div v-show="viewMode === 'card'" class="cardView">
      <div v-if="this.isBusy">
        <VueLoader style="height: 200px" text="Loading Projects..."/>
      </div>
      <div v-else>
        <PhotoProjectCard
          v-if="allProjects && allProjects.length > 0"
          class="projectCardView"
          :projects="allProjects"
        ></PhotoProjectCard>
        <div v-else class="col text-center mt-2 mb-2">
          {{ this.emptyFilteredText }}
        </div>
      </div>
    </div>

    <div class="card-footer">
      <div class="row justify-content-between">
        <div class="my-1 col-md-3">
          Showing {{ fromItem }} to {{ toItem }} of {{ totalItems }} projects
        </div>

        <div class="col-md-3 my-1">
          <b-pagination
            v-if="totalItems > 0"
            :total-rows="totalItems"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0 pagination-holder"
            prev-text="Prev"
            next-text="Next"
            align="fill"
          />
        </div>

        <div class="col-md-3 my-1">
          <b-form-group
            label="Per Page"
            label-align="right"
            label-cols-sm="7"
            label-cols-md="8"
            label-for="perPageSelect"
            class="mb-0"
          >
            <b-form-select
              v-model="perPage"
              id="perPageSelect"
              size="sm"
              :options="pageOptions"
              align="right"
            >
            </b-form-select>
          </b-form-group>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ProjectJoinButton from "../../assets/scripts/project/projectJoinButton.vue";
import PhotoProjectCard from "../layout/PhotoProjectCardList.vue";
import VueLoader from "../layout/Loader.vue";
import {VueperSlides, VueperSlide} from "vueperslides";

export default {
  components: {
    VueLoader,
    ProjectJoinButton,
    VueperSlides,
    VueperSlide,
    PhotoProjectCard,
  },

  data() {
    return {
      viewMode: "card",
      slidesData: [
        {id: 1, title: "", content: "All Topics", icon: "th-large"},
        {id: 2, title: "Air", icon: "wind"},
        {id: 3, title: "Animals", icon: "paw"},
        {id: 4, title: "Birds", icon: "dove"},
        {id: 5, title: "Climate and Weather", icon: "cloud"},
        {id: 6, title: "Energy", icon: "plug"},
        {id: 7, title: "Environment", icon: "tree"},
        {id: 8, title: "Food and Ag", icon: "pepper-hot"},
        {id: 9, title: "Geology", icon: "gem"},
        {id: 10, title: "Invertebrates", icon: "bug"},
        {id: 11, title: "Marine", icon: "water"},
        {id: 12, title: "Microbiology", icon: "microscope"},
        {id: 13, title: "Plants and Fungi", icon: "leaf"},
        {id: 14, title: "Social Science", icon: "hand-holding-heart"},
        {id: 15, title: "Space", icon: "rocket"},
        {id: 16, title: "Water", icon: "tint"},
      ],
      organizations: [],
      isBusy: false,
      emptyText: "No Projects Available",
      emptyFilteredText: "No Projects Available for Search/Filter Criteria",
      originalItemCount: 0,
      pageOptions: [12, 24, 60, 120],
      filterObject: {
        filterText: null,
        filterOrg: null,
        filterTopic: null,
      },
      // Boolean indicating if the advanced filter is toggled
      advFilterVisible: false,

      sortOptions: [
        {text: 'Activity: Newest - Oldest', value: {col: 'lastObservationDatetime', direction: 'desc'}},
        {text: 'Activity: Oldest - Newest', value: {col: 'lastObservationDatetime', direction: 'asc'}},
        {text: 'Project Name: A - Z', value: {col: 'name', direction: 'asc'}},
        {text: 'Project Name: Z - A', value: {col: 'name', direction: 'desc'}},
        {text: 'Start Date: Oldest - Newest', value: {col: 'createdAt', direction: 'asc'}},
        {text: 'Start Date: Newest - Oldest', value: {col: 'createdAt', direction: 'desc'}},
        {text: 'Measurements: High - Low', value: {col: 'measurementsTotal', direction: 'desc'}},
        {text: 'Measurements: Low - High', value: {col: 'measurementsTotal', direction: 'asc'}}
      ],
      currentSort: null,

      projectsListFields: [
        {key: "photo", label: "", sortable: false},
        {key: "name", label: "Project Name", sortable: false},
        {key: "createdAt", label: "Start Date", sortable: false},
        {key: "lastObservationDatetime", label: "Activity", sortable: false},
        {key: "measurementsTotal", label: "Measurements", sortable: false},
        {key: "options", label: "Options"},
      ],
    };
  },

  created() {
    // Hubs are fetched
    this.getHubs();

    // Initialize currentSort from store values when component is created
    this.currentSort = {col: this.itemSortBy, direction: this.itemSortDirection};
  },

  computed: {
    ...mapGetters({
      isOrganizationsLoading: "organization/list/isOrganizationsLoading",
      organizationsList: "organization/list/organizations",
      featuredHubs: "organization/list/featuredHubs",
      organizationsError: "organization/list/organizationsError",

      isLoading: "project/list/isLoading",
      allProjects: "project/list/projects",
      error: "project/list/error",

      itemPage: "project/list/page",
      itemPerPage: "project/list/perPage",
      itemTotal: "project/list/total",
      itemFilterText: "project/list/searchFilterText",
      itemFilterOrg: "project/list/searchFilterOrg",
      itemFilterTopic: "project/list/searchFilterTopic",
      itemSortBy: "project/list/sortBy",
      itemSortDirection: "project/list/sortDirection",
      itemSortDesc: "project/list/sortDesc",
    }),
    currentPage: {
      get() {
        return this.itemPage;
      },
      set(newValue) {
        this.setItemsPage(newValue);
      },
    },
    perPage: {
      get() {
        return this.itemPerPage;
      },
      set(newValue) {
        this.setItemsPerPage(newValue);
      },
    },
    totalItems: {
      get() {
        return this.itemTotal;
      },
      set(newValue) {
        this.setItemsTotal(newValue);
      },
    },
    filterText: {
      // Computed value for the search text
      get() {
        return this.itemFilterText;
      },
      set(newValue) {
        this.setSearchText(newValue);
      },
    },
    filterOrg: {
      // Computed value for the hub filter
      get() {
        return this.itemFilterOrg;
      },
      set(newValue) {
        this.setSearchOrg(newValue);
      },
    },
    filterTopic: {
      // Computed value for the topic filter
      get() {
        return this.itemFilterTopic;
      },
      set(newValue) {
        this.setSearchTopic(newValue);
      },
    },
    sortBy: {
      get() {
        return this.itemSortBy;
      },
      set(newValue) {
        this.setSortBy(newValue);
      },
    },
    sortDirection: {
      get() {
        return this.itemSortDirection;
      },
      set(newValue) {
        this.setSortDirection(newValue);
      },
    },
    sortDesc: {
      get() {
        return this.itemSortDesc;
      },
      set(newValue) {
        this.setSortDesc(newValue);
      },
    },
    fromItem() {
      return this.totalItems > 0
        ? (this.currentPage - 1) * this.perPage + 1
        : 0;
    },
    toItem() {
      return this.totalItems < this.currentPage * this.perPage
        ? this.totalItems
        : this.currentPage * this.perPage;
    },

    // Subset the orgArray for only the name
    // this is used to populate the picklist
    hubsArray() {
      let hubsIdName = [];
      hubsIdName[0] = {value: null, text: "By Hub", disabled: true};
      let j = 1;
      for (let i = 0; i < this.organizationsList.length; i++) {
        hubsIdName[j] = {
          value: this.organizationsList[i].url,
          text: this.organizationsList[i].name,
        };
        j++;
      }
      return hubsIdName;
    },

    filterVal: {
      get: function () {
        // This is our multi-filter computed value
        // - search by name|description|getting started|tasks of the project
        // - filter by organization
        // - filter by topic
        // More filters can be added here
        // - also add an equivalent to the store

        let retVal = "";
        if (this.filterText) {
          // Search across name, description, getting started and tasks
          retVal =
            retVal.length === 0
              ? "searchAll=" + this.filterText
              : retVal + "&searchAll=" + this.filterText;
        }
        if (this.filterOrg) {
          retVal =
            retVal.length === 0
              ? "organizationProjects.organization.url=" + this.filterOrg
              : retVal + "&organizationProjects.organization.url=" + this.filterOrg;
        }
        if (this.filterTopic) {
          retVal =
            retVal.length === 0
              ? "topics=" + this.filterTopic
              : retVal + "&topics=" + this.filterTopic;
        }
        return retVal;
      },
    },

    selectedSort: {
      get() {
        return this.currentSort
      },
      set(newVal) {
        // Only update if the sort values have actually changed
        if (this.currentSort.col !== newVal.col || this.currentSort.direction !== newVal.direction) {
          this.currentSort = newVal
          this.sortBy = newVal.col
          this.sortDirection = newVal.direction
          this.sortDesc = newVal.direction === 'desc'
        }
      }
    },
  },

  methods: {
    ...mapActions({
      getHubs: "organization/list/getOrganizations",
      getProjects: "project/list/get",
      resetProjects: "project/list/refresh",
      setItemsPage: "project/list/setPage",
      setItemsPerPage: "project/list/setPerPage",
      setItemsTotal: "project/list/setTotal",
      setSearchText: "project/list/setFilterText",
      setSearchOrg: "project/list/setFilterOrg",
      setSearchTopic: "project/list/setFilterTopic",
      setSortBy: "project/list/setSortBy",
      setSortDirection: "project/list/setSortDirection",
      setSortDesc: "project/list/setSortDesc",
    }),

    selectTopic(topic) {
      if (!topic) {
        this.filterTopic = ""
      } else {
        this.filterTopic = topic
      }
    },

    projects(ctx) {
      this.isBusy = true;
      let urlParams = `${this.urlBuilder(ctx)}`;
      return this.getProjects({urlParams: urlParams}).then((data) => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = ctx.sortBy || "";
        this.isBusy = false;
        return data["hydra:member"].map((projects) => {
          return {
            ...projects,
          };
        });
      });
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      if (this.totalItems !== filteredItems.length && filteredItems.length > this.perPage) {
        this.totalItems = filteredItems.length;
        this.currentPage = 1;
      }
    },

    onCreateClick() {
      // Check if user is verified
      if (JSON.parse(localStorage.getItem("sessionData")) !== null) {
        if (!JSON.parse(localStorage.getItem("sessionData")).isVerified) {
          this.$bvModal.show("not-verified-modal");
        } else {
          this.$router.push({name: "ProjectCreate"});
        }
      } else {
        this.$router.push({
          name: "Login",
          query: {redirect: "/projects/create"},
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.label-text {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: normal;
  color: #ffffff;
}

@media screen and (max-width: 1190px) {
  .label-text {
    display: none;
  }
}

// Search box styles
.form-control {
  border-color: transparent;
  box-shadow: none;
}

.search-box-border {
  border: solid 1px grey;
  border-radius: 16px;
}

.btn-secondary:disabled {
  cursor: not-allowed;
}

.vueperslides {
  @media (max-width: 768px) {
    overflow: hidden;
  }
}

.myVueperSlide {
  background-color: #ffffff !important;
  color: #000 !important;
}

.myVueperSlide.a:active {
  background-color: #87983f !important;
  color: #ffffff;
}

.btn-outline-secondary {
  background-color: #ffffff !important;
  color: #6c757d !important;
}

.btn-outline-secondary:hover {
  background-color: #87983f !important;
  color: white !important;
}

a.vueperslide {
  text-decoration: none !important;

  &:hover {
    background-color: #87983F;
    border: solid 2px #D6D6D6;
    color: #ffffff;
  }
}

.slide-container {
  max-height: 170px;
}

.slide-arrow {
  font-size: 20pt;
  color: #1b1b1b;
}

.slide-hover-unselected {
  background-color: #ffffff;
  border: solid 2px #D6D6D6;
  color: #A5B26F;
}

.slide-hover-selected {
  background-color: #87983F;
  border: solid 2px #D6D6D6;
  color: #ffffff;
}

.filter-container {
  background-color: #f7f6f6;
  border: solid 1px #d6d6d6;
}

</style>
